import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { firebaseApp } from './app';
import { SiroccoDataError } from './rtdb';
import { langue, t } from '../i18n';

/**
 * Les fonctions serveur, vues du client.
 *
 * Publier un post ou une reponse ne se fait plus en ecrivant dans la
 * base : les regles le refusent desormais. Le texte part vers une Cloud
 * Function qui l'examine, et n'ecrit que s'il passe. Un filtre pose ici,
 * dans le telephone, se contournerait en appelant la base directement.
 *
 * La region suit celle de la base : un aller-retour Belgique–Belgique
 * plutot qu'un detour par l'Iowa.
 */
const REGION = 'europe-west1';

let instance: Functions | null = null;

function fonctions(): Functions {
  instance ??= getFunctions(firebaseApp(), REGION);
  return instance;
}

/**
 * Appelle une fonction et traduit ses refus.
 *
 * Le serveur renvoie deja une phrase dans la langue de l'application :
 * elle part avec chaque appel, parce que celle du compte n'est pas
 * encore ecrite a l'inscription, et que celle du telephone n'est pas
 * forcement celle qu'on a choisie. On garde sa phrase telle quelle. Ce
 * qu'il ne renvoie jamais, c'est le mot qui a declenche le refus — sans
 * quoi on chercherait l'orthographe qui passe.
 */
export async function appeler<E extends object, S>(nom: string, donnees: E): Promise<S> {
  try {
    const reponse = await httpsCallable<E & { langue: string }, S>(
      fonctions(),
      nom,
    )({ ...donnees, langue });
    return reponse.data;
  } catch (cause) {
    const erreur = cause as { code?: string; message?: string };
    const code = (erreur.code ?? '').replace('functions/', '');

    if (code === 'failed-precondition') {
      throw new SiroccoDataError(
        erreur.message ?? t.commun.serveur.refuse,
        'content-refused',
        'content',
      );
    }
    if (code === 'invalid-argument') {
      throw new SiroccoDataError(
        erreur.message ?? t.commun.serveur.invalide,
        'invalid-content',
        'content',
      );
    }
    /*
      Un refus d'acces dit pourquoi. Le serveur ecrit deja la phrase, et
      elle vaut mieux que « reessaie dans un instant » : ce n'est pas une
      panne passagere, c'est une porte fermee.
    */
    if (code === 'permission-denied' || code === 'unauthenticated') {
      throw new SiroccoDataError(erreur.message ?? t.commun.serveur.accesRefuse, code);
    }

    throw new SiroccoDataError(
      t.commun.serveur.indisponible,
      code || 'unknown',
    );
  }
}
